/**
 * Fetches a fresh CSRF token from Craft's session-info endpoint
 * for custom JavaScript requests.
 *
 * Do not use this to update rendered form inputs. Sprig/Craft forms render
 * their own CSRF token and htmx headers; changing them later can desync the
 * form from the CSRF cookie/session state that existed when it was rendered.
 */
const refreshCsrfToken = {
    /** Resolved token: { name, value } or null */
    token: null,
    _promise: null,

    init() {
        // Deduplicate concurrent calls
        if (this._promise) return this._promise;

        this._promise = fetch('/actions/users/session-info', {
            headers: {
                Accept: 'application/json',
                'X-Requested-With': 'XMLHttpRequest'
            }
        })
            .then((response) => response.json())
            .then((data) => {
                const tokenName = data.csrfTokenName;
                const tokenValue = data.csrfTokenValue;

                if (!tokenName || !tokenValue) {
                    console.error(
                        'refreshCsrfToken: no CSRF data in session-info response'
                    );
                    return;
                }

                this.token = { name: tokenName, value: tokenValue };

                document.dispatchEvent(
                    new CustomEvent('csrf:refreshed', {
                        detail: { name: tokenName, value: tokenValue }
                    })
                );
            })
            .catch((error) => {
                console.error('Error fetching CSRF token:', error);
            })
            .finally(() => {
                this._promise = null;
            });

        return this._promise;
    },

    /**
     * Returns the current token synchronously, or null if not yet fetched.
     * @returns {{ name: string, value: string } | null}
     */
    getToken() {
        return this.token;
    },

    /**
     * Returns a promise that resolves with the token.
     * If not yet fetched, triggers a fetch.
     * @returns {Promise<{ name: string, value: string } | null>}
     */
    async getTokenAsync(forceRefresh = false) {
        if (this.token && !forceRefresh) return this.token;

        // A forced refresh must not fall back to a token from the expired session.
        if (forceRefresh) this.token = null;

        await this.init();
        return this.token;
    }
};

export default refreshCsrfToken;
